import { z } from 'zod'

import { api } from '#admin/lib/api-client.ts'

/**
 * The service categories and the installation fee of each, in each currency.
 *
 * Before the on-site date, an order charges a category's fee only when no earlier submitted order
 * of the same exhibitor charged it. From the date on, every order charges it. A category with no
 * active fee charges nothing.
 */
export const installationFeeSchema = z.object({
  id: z.string(),
  categoryId: z.string(),
  category: z.string(),
  currency: z.string(),
  amount: z.number(),
  taxRate: z.string(),
  isActive: z.boolean(),
})

export const serviceCategorySchema = z.object({
  id: z.string(),
  name: z.string(),
  /** The services in the catalog with this category. */
  serviceCount: z.number(),
  /** One for each currency. */
  fees: z.array(installationFeeSchema),
})

export type ServiceCategory = z.infer<typeof serviceCategorySchema>

/** A fee in one currency. Amounts and rates go over the wire as decimal strings, never as floats. */
export type FeeInput = {
  currency: string
  amount: string
  taxRate: string
  isActive: boolean
}

const categoriesBase = '/v1/exhibitor/admin/service-categories'
const categoryName = z.object({ id: z.string(), name: z.string() })

export function fetchServiceCategories(
  signal?: AbortSignal,
): Promise<{ items: ServiceCategory[] }> {
  return api.get(categoriesBase, {
    schema: z.object({ items: z.array(serviceCategorySchema) }),
    ...(signal ? { signal } : {}),
  })
}

export function createServiceCategory(name: string, fees: readonly FeeInput[]) {
  return api.post(categoriesBase, { schema: categoryName, body: { name, fees } })
}

/** A currency that is not in `fees` keeps its fee. */
export function updateServiceCategory(id: string, name: string, fees: readonly FeeInput[]) {
  return api.patch(`${categoriesBase}/${id}`, { schema: categoryName, body: { name, fees } })
}

/** Refused with `conflict` while a service has the category. */
export function deleteServiceCategory(id: string) {
  return api.delete(`${categoriesBase}/${id}`, { schema: z.object({ deleted: z.boolean() }) })
}
